import { useState, useEffect, useRef, type ComponentType, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import UsersPanel from '../components/settings/UsersPanel';
import TaxCodesPanel from '../components/settings/TaxCodesPanel';
import {
  Building2,
  Settings as SettingsIcon,
  AlertCircle,
  Save,
  RefreshCw,
  UserCog,
  Percent,
  ImageIcon,
} from 'lucide-react';
import { getCompany, updateCompany, uploadCompanyLogo, removeCompanyLogo } from '../utils/companyApi';
import { getMe, storeAuthSession } from '../utils/auth';
import { canManageUsers, getUserRole } from '../utils/rolePermissions';
import { ApiValidationError } from '../types/auth';
import type { Company, UpdateCompanyData } from '../types/company';
import { toast, toastApiError } from '../utils/toast';
import {
  fetchAuthenticatedObjectUrl,
  revokeObjectUrl,
} from '../utils/authenticatedDownload';
import {
  COMPANY_LOGO_ACCEPT,
  validateCompanyLogoFile,
} from '../utils/companyLogoValidation';

type SettingsTab = 'company' | 'tax' | 'users';

const ALL_TABS: { id: SettingsTab; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'company', label: 'Company Info', icon: Building2 },
  { id: 'tax', label: 'Tax Codes', icon: Percent },
  { id: 'users', label: 'Users', icon: UserCog },
];

const CURRENCY_OPTIONS = [
  { code: 'LKR', label: 'LKR — Sri Lankan Rupee' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CNY', label: 'CNY — Chinese Yuan' },
];

interface CompanyFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  tax_number: string;
  currency: string;
  active: boolean;
  allow_negative_inventory: boolean;
  tax_enabled: boolean;
  books_closed_through: string;
}

interface FieldErrors {
  [key: string]: string[] | undefined;
}

const canUpdateCompany = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const toDateInputValue = (value: string | null | undefined) => (value ? value.slice(0, 10) : '');

const companyToForm = (company: Company): CompanyFormData => ({
  name: company.name,
  email: company.email ?? '',
  phone: company.phone ?? '',
  address: company.address ?? '',
  tax_number: company.tax_number ?? '',
  currency: company.currency,
  active: company.active,
  allow_negative_inventory: company.allow_negative_inventory ?? false,
  tax_enabled: company.tax_enabled ?? true,
  books_closed_through: toDateInputValue(company.books_closed_through),
});

const formatDateTime = (value: string) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const resolveInitialTab = (tabParam: string | null): SettingsTab => {
  if (tabParam === 'users' && canManageUsers(getUserRole())) {
    return 'users';
  }
  if (tabParam === 'tax') {
    return 'tax';
  }
  return 'company';
};

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>(() =>
    resolveInitialTab(searchParams.get('tab'))
  );
  const [company, setCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoPreviewLoading, setLogoPreviewLoading] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isAdmin = canUpdateCompany();
  const tabs = ALL_TABS.filter((tab) => tab.id !== 'users' || canManageUsers(getUserRole()));

  useEffect(() => {
    if (activeTab === 'company' && !company) {
      loadCompany();
    }
  }, [activeTab, company]);

  useEffect(() => {
    const logoUrl = company?.logo_url ?? null;
    if (!logoUrl) {
      setLogoPreviewUrl(null);
      setLogoPreviewLoading(false);
      setLogoPreviewError(null);
      return;
    }

    let cancelled = false;
    setLogoPreviewLoading(true);
    setLogoPreviewError(null);
    setLogoPreviewUrl(null);

    fetchAuthenticatedObjectUrl(logoUrl)
      .then((url) => {
        if (cancelled) {
          revokeObjectUrl(url);
          return;
        }
        setLogoPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setLogoPreviewError('Could not load logo preview.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLogoPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [company?.logo_url]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    setError(null);
    if (tab === 'company') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const loadCompany = async () => {
    try {
      setCompanyLoading(true);
      setError(null);
      const data = await getCompany();
      setCompany(data);
      setFormData(companyToForm(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company profile');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  };

  const buildPatchPayload = (): UpdateCompanyData | null => {
    if (!company || !formData) return null;

    const payload: UpdateCompanyData = {};
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedAddress = formData.address.trim();
    const trimmedTax = formData.tax_number.trim();

    if (trimmedName !== company.name) payload.name = trimmedName;
    if (trimmedEmail !== (company.email ?? '')) payload.email = trimmedEmail || null;
    if (trimmedPhone !== (company.phone ?? '')) payload.phone = trimmedPhone || null;
    if (trimmedAddress !== (company.address ?? '')) payload.address = trimmedAddress || null;
    if (trimmedTax !== (company.tax_number ?? '')) payload.tax_number = trimmedTax || null;
    if (formData.currency !== company.currency) payload.currency = formData.currency;
    if (formData.active !== company.active) payload.active = formData.active;
    if (formData.allow_negative_inventory !== (company.allow_negative_inventory ?? false)) {
      payload.allow_negative_inventory = formData.allow_negative_inventory;
    }
    if (formData.tax_enabled !== (company.tax_enabled ?? true)) {
      payload.tax_enabled = formData.tax_enabled;
    }

    const formBooksClosed = formData.books_closed_through.trim() || null;
    const companyBooksClosed = company.books_closed_through
      ? toDateInputValue(company.books_closed_through)
      : null;
    if (formBooksClosed !== companyBooksClosed) {
      payload.books_closed_through = formBooksClosed;
    }

    return Object.keys(payload).length > 0 ? payload : null;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAdmin || !formData) return;

    setError(null);
    setFieldErrors({});

    if (!formData.name.trim()) {
      setFieldErrors({ name: ['Company name is required'] });
      return;
    }

    const payload = buildPatchPayload();
    if (!payload) {
      setError('No changes to save.');
      return;
    }

    try {
      setSubmitting(true);
      await updateCompany(payload);
      const verified = await getCompany();
      setCompany(verified);
      setFormData(companyToForm(verified));

      const token = localStorage.getItem('token');
      if (token) {
        const user = await getMe();
        storeAuthSession(token, user);
      }

      if (
        payload.allow_negative_inventory !== undefined &&
        (verified.allow_negative_inventory ?? false) !== payload.allow_negative_inventory
      ) {
        setError(
          'The inventory setting was not saved by the server. Confirm the API includes negative-stock support (migration 0041) and that VITE_API_BASE_URL points at that server.'
        );
        toast.error('Inventory setting was not saved — check API deployment and migrations.');
        return;
      }

      toast.success('Company profile updated successfully.');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to update company profile');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (company) {
      setFormData(companyToForm(company));
      setFieldErrors({});
      setError(null);
    }
  };

  const refreshCompany = async () => {
    const verified = await getCompany();
    setCompany(verified);
    setFormData(companyToForm(verified));
    return verified;
  };

  const handleLogoSelect = async (file: File | null) => {
    if (!isAdmin || !file) return;

    const validation = validateCompanyLogoFile(file);
    if (!validation.valid) {
      toast.error(validation.message);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      return;
    }

    try {
      setLogoBusy(true);
      setError(null);
      await uploadCompanyLogo(file);
      await refreshCompany();
      toast.success('Company logo updated.');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to upload company logo');
      }
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleLogoRemove = async () => {
    if (!isAdmin || !company?.logo_url) return;

    try {
      setLogoBusy(true);
      setError(null);
      await removeCompanyLogo();
      await refreshCompany();
      toast.success('Company logo removed.');
    } catch (err) {
      toastApiError(err, 'Failed to remove company logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const inputClass = (field: string) =>
    `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400 ${
      fieldError(field) ? 'border-red-300' : 'border-gray-200'
    } ${!isAdmin ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : 'bg-white'}`;

  return (
    <div className="h-screen bg-gray-100 flex w-full overflow-hidden">
      <Sidebar />

      <main className="flex-1 p-4 md:p-6 overflow-y-auto min-w-0">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-800 rounded-lg">
              <SettingsIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Settings</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                Manage your company profile and application preferences.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-wrap gap-1 p-2 border-b border-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'company' && companyLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
            </div>
          )}

          {activeTab === 'company' && !companyLoading && formData && company && (
            <div className="p-4 md:p-6">
              {!isAdmin && (
                <div className="mb-6 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>
                    You can view company details here. Only administrators can make changes.
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Company logo</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      Printed on invoice, quotation, purchase order, and payment receipt PDFs.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="w-32 h-32 shrink-0 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
                      {logoPreviewLoading || logoBusy ? (
                        <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
                      ) : logoPreviewUrl ? (
                        <img
                          src={logoPreviewUrl}
                          alt={`${company.name} logo`}
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-gray-400 px-2 text-center">
                          <ImageIcon className="h-8 w-8" />
                          <span className="text-xs">No logo</span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-2 min-w-0">
                      {isAdmin && (
                        <>
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept={COMPANY_LOGO_ACCEPT}
                            className="hidden"
                            disabled={logoBusy || submitting}
                            onChange={(e) =>
                              void handleLogoSelect(e.target.files?.[0] ?? null)
                            }
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={logoBusy || submitting}
                              onClick={() => logoInputRef.current?.click()}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
                            >
                              {company.logo_url ? 'Replace logo' : 'Upload logo'}
                            </button>
                            {company.logo_url && (
                              <button
                                type="button"
                                disabled={logoBusy || submitting}
                                onClick={() => void handleLogoRemove()}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-red-700 bg-white hover:bg-red-50 disabled:opacity-60"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      <p className="text-xs text-gray-500">
                        JPEG, PNG, or GIF. Uploading replaces the current logo.
                      </p>
                      {logoPreviewError && (
                        <p className="text-sm text-red-600">{logoPreviewError}</p>
                      )}
                      {fieldError('file') && (
                        <p className="text-sm text-red-600">{fieldError('file')}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Company name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    maxLength={200}
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isAdmin}
                    className={inputClass('name')}
                    placeholder="Legal or trading name"
                  />
                  {fieldError('name') && (
                    <p className="mt-1 text-xs text-red-600">{fieldError('name')}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      maxLength={150}
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className={inputClass('email')}
                      placeholder="info@company.com"
                    />
                    {fieldError('email') && (
                      <p className="mt-1 text-xs text-red-600">{fieldError('email')}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      maxLength={30}
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className={inputClass('phone')}
                      placeholder="+94771234567"
                    />
                    {fieldError('phone') && (
                      <p className="mt-1 text-xs text-red-600">{fieldError('phone')}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    rows={3}
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isAdmin}
                    className={inputClass('address')}
                    placeholder="Mailing or registered address"
                  />
                  {fieldError('address') && (
                    <p className="mt-1 text-xs text-red-600">{fieldError('address')}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="tax_number"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Tax number (TIN)
                    </label>
                    <input
                      id="tax_number"
                      name="tax_number"
                      type="text"
                      maxLength={50}
                      value={formData.tax_number}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className={inputClass('tax_number')}
                      placeholder="Tax identification number"
                    />
                    {fieldError('tax_number') && (
                      <p className="mt-1 text-xs text-red-600">{fieldError('tax_number')}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="currency"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Currency
                    </label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className={inputClass('currency')}
                    >
                      {CURRENCY_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                      {!CURRENCY_OPTIONS.some((o) => o.code === formData.currency) && (
                        <option value={formData.currency}>{formData.currency}</option>
                      )}
                    </select>
                    {fieldError('currency') && (
                      <p className="mt-1 text-xs text-red-600">{fieldError('currency')}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Changing currency affects display only; existing amounts are not converted.
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Tax</h3>
                  <div className="flex items-start gap-3">
                    <input
                      id="tax_enabled"
                      name="tax_enabled"
                      type="checkbox"
                      checked={formData.tax_enabled}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-800 focus:ring-slate-800 disabled:opacity-60"
                    />
                    <div>
                      <label htmlFor="tax_enabled" className="text-sm text-gray-700">
                        Enable VAT on invoices and bills
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        When disabled, line tax is zero on new drafts; existing posted documents
                        are not recalculated. Tax code selection on invoice and bill lines is
                        rejected by the API.
                      </p>
                      {fieldError('tax_enabled') && (
                        <p className="mt-1 text-xs text-red-600">{fieldError('tax_enabled')}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Inventory</h3>
                  <div className="flex items-start gap-3">
                    <input
                      id="allow_negative_inventory"
                      name="allow_negative_inventory"
                      type="checkbox"
                      checked={formData.allow_negative_inventory}
                      onChange={handleInputChange}
                      disabled={!isAdmin}
                      className="h-4 w-4 mt-0.5 rounded border-gray-300 text-slate-800 focus:ring-slate-800 disabled:opacity-60"
                    />
                    <div>
                      <label htmlFor="allow_negative_inventory" className="text-sm text-gray-700">
                        Allow selling below on-hand quantity (admin invoices only)
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        When enabled, administrators can post invoices that oversell inventory. COGS
                        for backordered units is estimated until stock is received via a bill or
                        adjustment.
                      </p>
                      {fieldError('allow_negative_inventory') && (
                        <p className="mt-1 text-xs text-red-600">
                          {fieldError('allow_negative_inventory')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Books closing</h3>
                  <div>
                    <label
                      htmlFor="books_closed_through"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Books closed through
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="books_closed_through"
                        name="books_closed_through"
                        type="date"
                        value={formData.books_closed_through}
                        onChange={handleInputChange}
                        disabled={!isAdmin}
                        className={`${inputClass('books_closed_through')} max-w-xs`}
                      />
                      {isAdmin && formData.books_closed_through && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) =>
                              prev ? { ...prev, books_closed_through: '' } : prev
                            )
                          }
                          className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Last day of the last closed fiscal year (e.g. 31 Dec 2025 after year-end
                      close). Leave blank for no book close. Controls GL reversal dating on invoice
                      void and amend.
                    </p>
                    {fieldError('books_closed_through') && (
                      <p className="mt-1 text-xs text-red-600">
                        {fieldError('books_closed_through')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    id="active"
                    name="active"
                    type="checkbox"
                    checked={formData.active}
                    onChange={handleInputChange}
                    disabled={!isAdmin}
                    className="h-4 w-4 rounded border-gray-300 text-slate-800 focus:ring-slate-800 disabled:opacity-60"
                  />
                  <label htmlFor="active" className="text-sm text-gray-700">
                    Company is active
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>
                    <span className="font-medium text-gray-600">Company ID:</span>{' '}
                    <span className="tabular-nums">{company.id}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Created:</span>{' '}
                    {formatDateTime(company.created_at)}
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Last updated:</span>{' '}
                    {formatDateTime(company.updated_at)}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Saving…
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={submitting}
                      className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 disabled:opacity-60"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}

          {activeTab === 'users' && <UsersPanel />}
          {activeTab === 'tax' && <TaxCodesPanel />}
        </div>
      </main>
    </div>
  );
};

export default Settings;
